/**
 * Date formatting primitive shared by the user-preference formatters.
 *
 * The `timeZone` argument is the whole point of this module: it names the frame a
 * `Date` is READ in, and picking the wrong one shifts the rendered calendar day.
 * There are exactly two correct choices, and which one applies is decided by the
 * VALUE being rendered, not by the component doing the rendering:
 *
 *   * An INSTANT (a `TIMESTAMPTZ` — created_at, shipped_at, a meeting time) is a
 *     point on the world clock. Render it in the user's CONFIGURED zone, so they
 *     see the wall-clock time they'd have seen standing there.
 *
 *   * A DATE-ONLY value (a `DATE` — a planning start/end date, a due date) carries
 *     no instant at all. It is parsed to local midnight (`parseISO`) and written
 *     back from the local frame (`format(d, 'yyyy-MM-dd')`), so it must be READ in
 *     that same local frame — pass `timeZone: undefined` and `Intl` uses the
 *     ambient zone. The round-trip is then exact by construction, and the day shown
 *     always equals the day stored. Rendering such a value in the configured zone
 *     is what produced the roadmap's off-by-one-day labels.
 */

export type DateStyle = 'short' | 'medium' | 'long' | 'full';
export type DateFormatPattern = 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd';

/**
 * Format `date` per the user's date-format preference.
 *
 * @param timeZone IANA zone to read the date in, or `undefined` for the ambient
 *   browser frame (the correct choice for date-only values — see above).
 */
export function formatDateInZone(
  date: Date,
  style: DateStyle,
  dateFormat: DateFormatPattern,
  timeZone: string | undefined,
): string {
  if (isNaN(date.getTime())) return '';

  // ISO-like output is assembled from parts rather than a locale pattern, so it
  // is stable across engines.
  if (dateFormat === 'yyyy-mm-dd') {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    return `${year}-${month}-${day}`;
  }

  const options: Intl.DateTimeFormatOptions = { timeZone };

  switch (style) {
    case 'short':
      options.day = '2-digit';
      options.month = '2-digit';
      options.year = '2-digit';
      break;
    case 'medium':
      options.day = '2-digit';
      options.month = '2-digit';
      options.year = 'numeric';
      break;
    case 'long':
      options.day = 'numeric';
      options.month = 'long';
      options.year = 'numeric';
      break;
    case 'full':
      options.weekday = 'long';
      options.day = 'numeric';
      options.month = 'long';
      options.year = 'numeric';
      break;
  }

  // Locale chosen purely for day/month ORDER — en-US is month-first, en-GB day-first.
  const dateLocale = dateFormat === 'mm/dd/yyyy' ? 'en-US' : 'en-GB';
  return new Intl.DateTimeFormat(dateLocale, options).format(date);
}

/**
 * Serialize a `Date` to a `yyyy-MM-dd` string for a DATE-only column, reading the
 * LOCAL calendar day.
 *
 * This is the WRITE-side counterpart to `formatPlanningDate`/`parseISO` on the read
 * side. NEVER use `date.toISOString().split('T')[0]` for a `DATE` value: `toISOString`
 * converts to UTC first, so a local-midnight `Date` in an east-of-UTC zone (e.g.
 * Asia/Tokyo, +9) rolls back to the previous calendar day — the user picks Jul 14 and
 * the API stores Jul 13. Formatting from the Date's LOCAL `getFullYear`/`getMonth`/
 * `getDate` keeps the day the user actually chose, in every timezone, and round-trips
 * exactly with `parseISO` on the read side.
 */
export function toDateOnlyString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a DATE-only string (`yyyy-MM-dd`) into a `Date` at LOCAL midnight — the
 * READ-side counterpart to `toDateOnlyString`. `new Date("2026-07-14")` parses the
 * bare string as UTC midnight, so in any timezone behind UTC (e.g. America/New_York)
 * `.getDate()` rolls back to the previous calendar day (Jul 13) — the classic
 * date-only off-by-one. Building the `Date` from the y/m/d components keeps the
 * calendar day the value actually denotes, in every timezone, round-tripping with
 * `toDateOnlyString`. A value carrying a time component (a full ISO instant) has no
 * date-only ambiguity and is parsed as-is.
 */
export function parseDateOnlyToLocal(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    const [, year, month, day] = match.map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}
